/**
 * Where the sign-in guard (`routes/__root.tsx`) sends a signed-out visitor
 * back to after they sign in.
 *
 * The result is the requested pathname plus its search string, a same-origin
 * path that the guard hands to the sign-in page as `redirect_url`. The hash is
 * deliberately never read, so nothing in a fragment is carried through sign-in.
 * Clerk's `<SignIn>`/`<SignUp>` honour `redirect_url` over `fallbackRedirectUrl`.
 *
 * This is the first check on where sign-in may send the visitor: only a plain
 * same-origin path ever becomes a `redirect_url`. Clerk's own same-origin check
 * on `redirect_url` is the second line of defence, behind it.
 *
 * Returns `undefined` (sign-in falls back to `/`) when there is nothing worth
 * returning to: the home page, the sign-in / sign-up pages themselves, or a
 * pathname that is not a plain absolute path (`//host`, anything with a
 * backslash, a full URL, a relative path).
 *
 * `searchStr` is TanStack Router's own: `?a=b` or `''`.
 */
export function signInRedirectFor(location: { pathname: string; searchStr: string }): string | undefined {
    const { pathname, searchStr } = location;
    if (!pathname.startsWith('/') || pathname.startsWith('//')) return undefined;
    if (pathname.includes('\\')) return undefined;
    if (pathname === '/') return undefined;
    if (isAuthPagePath(pathname)) return undefined;
    return pathname + searchStr;
}

/** True for `/sign-in` and `/sign-up` and anything beneath them. */
function isAuthPagePath(pathname: string): boolean {
    return ['/sign-in', '/sign-up'].some((page) => pathname === page || pathname.startsWith(`${page}/`));
}

/** Search params of the sign-in and sign-up routes. */
export type SignInSearch = { redirect_url?: string };

/**
 * `validateSearch` for the sign-in and sign-up routes: a shallow copy of
 * `search` with every key kept (Clerk adds its own, such as `__clerk_status`),
 * except that a `redirect_url` that is not a string is dropped.
 */
export function keepSignInSearch(search: Record<string, unknown>): SignInSearch {
    const { redirect_url: redirectUrl, ...rest } = search;
    return typeof redirectUrl === 'string' ? { ...rest, redirect_url: redirectUrl } : { ...rest };
}
